import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck, Handshake, XCircle } from "lucide-react";
import Nav from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { API } from "@/contexts/AuthContext";

const PLAN_OPTIONS = ["Starter", "Creator", "Business", "Agency"];

function Field({ label, required, hint, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="text-[11.5px] text-slate-500">{hint}</span>}
    </label>
  );
}

const inputCls =
  "rounded-md border border-slate-300 bg-white px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1A4FFF]/25";

export default function AffiliatePage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    instagram_url: "",
    x_url: "",
    facebook_url: "",
    linkedin_url: "",
    followers_instagram: "",
    followers_x: "",
    followers_facebook: "",
    followers_linkedin: "",
    plan_to_promote: "Starter",
    how_they_know: "",
    why_trust: "",
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/affiliate/apply`, form);
      setOk(true);
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
        (Array.isArray(e?.response?.data?.detail)
          ? e.response.data.detail.map((x) => x?.msg).filter(Boolean).join(" · ")
          : null) ||
        e?.message ||
        "Could not submit your application."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-page">
      <Nav />

      <main className="max-w-3xl mx-auto px-5 sm:px-6 pt-24 pb-16">
        <header className="mb-8">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1628]/5 text-[#0A1628] px-3 py-1 text-[11.5px] font-semibold uppercase tracking-wider">
            <Handshake size={12} />
            Affiliate program
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 mt-4 tracking-tight">
            Bring Zynthoro to your audience.
          </h1>
          <p className="text-slate-600 text-[15px] mt-3 max-w-2xl">
            We work with creators, agencies and community builders who genuinely
            use Zynthoro. Apply below and we'll reach out personally if it's a
            good fit.
          </p>
        </header>

        <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 mb-8" data-testid="affiliate-scope">
          <div className="flex items-start gap-3">
            <ShieldCheck size={18} className="text-[#0A1628] mt-0.5 shrink-0" />
            <div>
              <p className="text-[13.5px] font-semibold text-slate-900">Program scope</p>
              <ul className="mt-2 space-y-1.5 text-[13px] text-slate-700">
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 size={13} className="text-emerald-600 mt-1 shrink-0" />
                  <span>Open to <strong>Starter, Creator, Business and Agency</strong> plans.</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <XCircle size={13} className="text-rose-500 mt-1 shrink-0" />
                  <span><strong>Kickstart is excluded.</strong> Kickstart is a lifetime deal, not a recurring plan.</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <XCircle size={13} className="text-rose-500 mt-1 shrink-0" />
                  <span><strong>Enterprise is sales-led</strong>, not affiliate. Please introduce us directly instead.</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {ok ? (
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center"
            data-testid="affiliate-success"
          >
            <CheckCircle2 size={22} className="mx-auto text-emerald-600 mb-2" />
            <p className="text-[15px] font-semibold text-emerald-900">
              Thanks — your application is in.
            </p>
            <p className="text-[13.5px] text-emerald-800 mt-1">
              We review every application by hand. If we can move forward,
              you'll hear back at <strong>{form.email}</strong>.
            </p>
            <p className="mt-4">
              <Link to="/" className="text-[#1A4FFF] font-semibold text-[13px] hover:underline">
                Back to home
              </Link>
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 space-y-5" data-testid="affiliate-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input required className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="af-name" />
              </Field>
              <Field label="Email" required>
                <input required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@example.com" data-testid="af-email" />
              </Field>
              <Field label="Company or website" required>
                <input required className={inputCls} value={form.company} onChange={set("company")} placeholder="doemedia.com" data-testid="af-company" />
              </Field>
              <Field label="Country" required>
                <input required className={inputCls} value={form.country} onChange={set("country")} placeholder="Netherlands" data-testid="af-country" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Instagram URL" required>
                <input required className={inputCls} value={form.instagram_url} onChange={set("instagram_url")} placeholder="https://instagram.com/…" data-testid="af-instagram" />
              </Field>
              <Field label="X (Twitter) URL" required>
                <input required className={inputCls} value={form.x_url} onChange={set("x_url")} placeholder="https://x.com/…" data-testid="af-x" />
              </Field>
              <Field label="Facebook URL" required>
                <input required className={inputCls} value={form.facebook_url} onChange={set("facebook_url")} placeholder="https://facebook.com/…" data-testid="af-facebook" />
              </Field>
              <Field label="LinkedIn URL">
                <input className={inputCls} value={form.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/… (optional)" data-testid="af-linkedin" />
              </Field>
            </div>

            <div>
              <p className="text-[12.5px] font-semibold text-slate-700 mb-2">Follower count per channel <span className="text-rose-500">*</span></p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Instagram" required>
                  <input required className={inputCls} value={form.followers_instagram} onChange={set("followers_instagram")} placeholder="e.g. 12k" data-testid="af-followers-ig" />
                </Field>
                <Field label="X" required>
                  <input required className={inputCls} value={form.followers_x} onChange={set("followers_x")} placeholder="e.g. 3k" data-testid="af-followers-x" />
                </Field>
                <Field label="Facebook" required>
                  <input required className={inputCls} value={form.followers_facebook} onChange={set("followers_facebook")} placeholder="e.g. 1k" data-testid="af-followers-fb" />
                </Field>
                <Field label="LinkedIn">
                  <input className={inputCls} value={form.followers_linkedin} onChange={set("followers_linkedin")} placeholder="optional" data-testid="af-followers-li" />
                </Field>
              </div>
            </div>

            <Field label="Which plan would you promote?" required>
              <select required className={inputCls} value={form.plan_to_promote} onChange={set("plan_to_promote")} data-testid="af-plan">
                {PLAN_OPTIONS.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </Field>

            <Field label="How do you know Zynthoro?" required hint="Even a short paragraph is fine.">
              <textarea required rows={3} className={inputCls} value={form.how_they_know} onChange={set("how_they_know")} placeholder="How you found us, how long you've been using it, what you like…" data-testid="af-how" />
            </Field>

            <Field label="Why should we trust you?" required hint="Past promotions, audience trust signals, anything relevant.">
              <textarea required rows={3} className={inputCls} value={form.why_trust} onChange={set("why_trust")} placeholder="Your track record, references, previous campaigns…" data-testid="af-why" />
            </Field>

            {err && (
              <p className="text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="affiliate-err">
                {err}
              </p>
            )}

            <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11.5px] text-slate-500">
                By submitting you confirm you've read our{" "}
                <Link to="/legal/privacy-policy" className="underline">Privacy Policy</Link>.
                We review every application by hand.
              </p>
              <button
                type="submit"
                disabled={busy}
                data-testid="affiliate-submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1A4FFF] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Handshake size={14} />}
                Send application
              </button>
            </div>
          </form>
        )}
      </main>

      <Footer />
    </div>
  );
}
